import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { friendlyMessage, showApiError } from '@/lib/errors';
import { useQueryClient } from '@tanstack/react-query';

export default function MetaCallback() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing Meta OAuth callback...');

  useEffect(() => {
    const handleMetaCallback = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        if (error) {
          if (window.opener && !window.opener.closed) {
            window.opener.postMessage({
              success: false,
              error: errorDescription || error
            }, '*');
          }
          throw new Error(errorDescription || error);
        }

        if (!code) {
          if (window.opener && !window.opener.closed) {
            window.opener.postMessage({
              success: false,
              error: 'No authorization code received from Meta'
            }, '*');
          }
          throw new Error('No authorization code received from Meta');
        }


        if (window.opener && !window.opener.closed) {
          window.opener.postMessage({
            success: true,
            eventData: {
              code,
              state
            }
          }, '*');

          setStatus('success');
          setMessage('Authorization successful! Closing window...');

          setTimeout(() => {
            window.close();
          }, 1000);

          return; // Exit early, don't do anything else
        } else {
          throw new Error('Parent window not found. Please try again.');
        }

      } catch (error: any) {
        setStatus('error');
        setMessage(friendlyMessage(error, "We couldn't finish connecting to Facebook. Please try again."));

        showApiError(error, {
          fallback: { title: "Meta OAuth Error", description: 'An error occurred during Meta OAuth callback' },
        });
      }
    };

    handleMetaCallback();
  }, [navigate, toast, queryClient]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="flex items-center justify-center gap-2">
            {status === 'loading' && <Loader2 className="h-5 w-5 animate-spin" />}
            {status === 'success' && <CheckCircle className="h-5 w-5 text-green-500" />}
            {status === 'error' && <XCircle className="h-5 w-5 text-red-500" />}
            Meta OAuth Callback
          </CardTitle>
          <CardDescription>
            {status === 'loading' && 'Processing your Meta OAuth callback...'}
            {status === 'success' && 'OAuth callback processed successfully!'}
            {status === 'error' && 'OAuth callback failed'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertDescription>{message}</AlertDescription>
          </Alert>

          {status === 'success' && (
            <div className="text-center text-sm text-gray-600">
              This window will close automatically...
            </div>
          )}

          {status === 'error' && (
            <div className="flex gap-2">
              <Button
                onClick={() => window.close()}
                variant="outline"
                className="flex-1"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Close Window
              </Button>
              <Button
                onClick={() => window.location.reload()}
                className="flex-1"
              >
                Try Again
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
